import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  PRERENDERED_AVATARS,
  mapCatalogRows,
  type PrerenderedAvatar,
} from "@/lib/prerenderedAvatars";

// Live avatar catalog from the `avatar_catalog` table (RLS only exposes
// is_active=true rows), ordered by sort_order.
//
// CONTRACT (task 96bc7d74 / PR A):
//  - The picker must NEVER depend on the network: while loading, and whenever
//    the query fails or returns zero usable rows, this hook serves the seed
//    array from `prerenderedAvatars.ts` (which stays in the code on purpose).
//  - Successful non-empty results are cached in memory for the SPA session
//    (module-level, same pattern as useAvatar) — one fetch per session.
//  - A failed/empty fetch is NOT cached, so a later mount can self-heal.

let cachedCatalog: PrerenderedAvatar[] | null = null;
let fetchPromise: Promise<PrerenderedAvatar[] | null> | null = null;

async function fetchCatalog(): Promise<PrerenderedAvatar[] | null> {
  try {
    const { data, error } = await (supabase as any)
      .from("avatar_catalog")
      .select("id, name, gender, file_base, sort_order")
      .eq("is_active", true)
      .order("sort_order", { ascending: true });
    if (error) {
      // Fallback keeps the picker fully functional (seed array) — warn level
      // per §11: expected failure with a REAL fallback, user intent preserved.
      console.warn("[useAvatarCatalog] avatar_catalog read failed:", error);
      return null;
    }
    const mapped = mapCatalogRows(data);
    if (mapped.length === 0) {
      console.warn("[useAvatarCatalog] avatar_catalog returned 0 usable rows — serving seed fallback");
      return null;
    }
    return mapped;
  } catch (err) {
    console.warn("[useAvatarCatalog] avatar_catalog fetch threw:", err);
    return null;
  }
}

export interface AvatarCatalogState {
  // Always non-empty: live rows when available, seed array otherwise.
  avatars: PrerenderedAvatar[];
  // True only during the first fetch of the session (no cache yet). The seed
  // array is already being served meanwhile, so consumers may ignore this.
  loading: boolean;
}

export function useAvatarCatalog(): AvatarCatalogState {
  const [avatars, setAvatars] = useState<PrerenderedAvatar[]>(
    () => cachedCatalog ?? PRERENDERED_AVATARS,
  );
  const [loading, setLoading] = useState<boolean>(() => cachedCatalog === null);

  useEffect(() => {
    if (cachedCatalog) return;
    let cancelled = false;

    // Dedupe concurrent mounts onto one in-flight fetch. Capture the promise
    // locally (useAvatar pattern) so a concurrent reset can't null it mid-await.
    let p = fetchPromise;
    if (!p) {
      p = fetchCatalog().then((result) => {
        if (result) cachedCatalog = result;
        fetchPromise = null;
        return result;
      });
      fetchPromise = p;
    }
    p.then((result) => {
      if (cancelled) return;
      setLoading(false);
      if (result) setAvatars(result);
      // null → keep serving the seed fallback already on screen.
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return { avatars, loading };
}

// Test/session hook: clears the module cache so the next mount refetches.
export function invalidateAvatarCatalogCache(): void {
  cachedCatalog = null;
  fetchPromise = null;
}
